import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { DocumentResource } from '@getaccept/lib-shared/src/resources/types/document-resource';
import { NodeType } from '@getaccept/editor-lib';
import type { Cell, Row, Section, Signature, Node } from '@getaccept/editor-lib';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import { FieldType } from '@getaccept/lib-shared/src/fields/types/enums/field-type';
import { FieldHelper } from '@getaccept/lib-shared/src/fields/helpers/field.helper';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import type { Field as PDFField } from '@getaccept/lib-shared/src/fields/types/field';

export class AssemblerHelper {
  static getSignableRecipients(recipients: Recipient[]) {
    return recipients.filter(
      (recipient: Recipient) => ![RecipientRole.CC].includes(recipient.role as RecipientRole)
    );
  }

  static getCurrentDocumentPageAfterPageDelete(
    deletedPageIndex: number,
    pages: DocumentPage[]
  ): DocumentPage {
    let nextCurrentDocumentPage: DocumentPage = pages[deletedPageIndex];
    if (!nextCurrentDocumentPage) {
      nextCurrentDocumentPage = pages[deletedPageIndex - 1];
    }
    return nextCurrentDocumentPage;
  }

  static updatePageNums(
    pages: DocumentPage[],
    newPageNums: Partial<DocumentPage>[]
  ): DocumentPage[] {
    return pages
      .map((page: DocumentPage) => ({
        ...page,
        pageNum: newPageNums.find(pageNum => pageNum.id === page.id).pageNum,
      }))
      .sort((a: DocumentPage, b: DocumentPage): number => a.pageNum - b.pageNum);
  }

  static mapDocumentResources(
    pages: DocumentPage[],
    documentResources: DocumentResource[]
  ): DocumentPage[] {
    return pages.map((page: DocumentPage) => {
      let resourceId = '';
      switch (page.pageType) {
        case DocumentPageType.Editor: {
          if (!page.editorBlock) {
            break;
          }
          resourceId = AssemblerHelper.findResourceId(documentResources, page.editorBlock.id);
          break;
        }
        case DocumentPageType.ContentLink: {
          if (!page.contentLinkBlock) {
            break;
          }
          resourceId = AssemblerHelper.findResourceId(documentResources, page.contentLinkBlock.id);
          break;
        }
        case DocumentPageType.Video: {
          if (!page.video) {
            break;
          }
          resourceId = AssemblerHelper.findResourceId(documentResources, page.video.id);
          break;
        }
        default:
          break;
      }
      return { ...page, resourceId };
    });
  }

  static sortRecipients(recipients: Recipient[]): Recipient[] {
    return recipients.sort((a: Recipient, b: Recipient) => {
      if (RecipientsHelper.isApprover(a.role)) {
        return -1;
      }
      if (
        !RecipientsHelper.isInternalApprover(b.role) ||
        a.role === RecipientRole.ExternalApprover
      ) {
        return 1;
      }
    });
  }

  static orderRecipients(recipients: Recipient[]): Partial<Recipient>[] {
    return recipients.map((recipient, i) => ({ id: recipient.id, orderNum: i + 1 }));
  }

  static reassignFields(fields: PDFField[], recipientId: string, connectFirstRecipient: boolean) {
    return fields.map(field => ({
      ...field,
      recipientId: recipientId,
      isCollectable: FieldHelper.isCollectable(
        recipientId,
        field.type,
        field.value,
        connectFirstRecipient
      ),
      source: FieldSource.Recipient,
      value: [FieldType.Signature, FieldType.Initials].includes(field.type)
        ? 'recipient'
        : field.value,
    }));
  }

  static getLabelName(recipient: Recipient): string {
    return RecipientsHelper.getRecipientFullName(recipient) || recipient.roleName;
  }

  private static findResourceId(resources: DocumentResource[], blockId: string): string {
    return resources.find((resource: DocumentResource) => resource.contentId === blockId)
      ?.resourceId;
  }

  static hasEditorSignatureField(document: Document): boolean {
    const signatureNodes: Node[] = [];
    document.pages?.some((page: DocumentPage) =>
      page.editorBlock?.content.sections.some((section: Section) =>
        section.rows.some((row: Row) =>
          row.cells.some((cell: Cell) =>
            cell.nodes.some((node: Node) => {
              if (AssemblerHelper.isSignatureNode(node)) {
                signatureNodes.push(node);
              }
              return signatureNodes.length > 0;
            })
          )
        )
      )
    );
    return !!signatureNodes.length;
  }

  private static isSignatureNode(node: Node): node is Signature {
    return node.type === NodeType.Signature;
  }

  static isRole(recipient: Recipient): boolean {
    return (
      !!recipient.roleName &&
      !recipient.firstName &&
      !recipient.lastName &&
      !recipient.email &&
      !recipient.mobile
    );
  }

  static getRecipientNameWithFallback(recipient: Recipient): string {
    return AssemblerHelper.isRole(recipient)
      ? recipient.roleName
      : RecipientsHelper.getRecipientFullName(recipient) || recipient.email || recipient.mobile;
  }
}
